import { UNIFIED_FORUMS_GROUPS_QUERY } from 'graphql/UnifiedForumsGroupsQuery';
import { UnifiedForumsGroupsQuery } from 'graphql/__generated__/UnifiedForumsGroupsQuery.graphql';
import { useLazyLoadQuery } from 'react-relay';
import { Link } from 'react-resource-router';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import TeamsIcon from 'assets/images/teams-icon.svg';
import EmptyStateMessageBody from 'components/common/section/sub-section/EmptyStateMessageBody';
import useIsPersonalView from 'utils/useIsPersonalView';
import ElevatedContainer from 'components/common/section/ElevatedContainer';
import SubSection from 'components/common/section/sub-section/SubSection';
import Fallback from 'components/common/fallback/Fallback';
import { logBoundaryError } from 'services/logger';
import { ErrorBoundary } from 'react-error-boundary';
import {
  trackGroupCardClick,
  trackJoinGroupClick,
} from 'analytics/groupsAnalytics';
import SectionSummaryContainer from 'components/common/section/SectionSummaryContainer';
import { LoadingMainPanelSection } from 'components/common/suspense-fallbacks/LoadingMainPanelSection';
import { Suspense } from 'react';
import { LoadingSidePanelSection } from 'components/common/suspense-fallbacks/LoadingSidePanelSection';
import { getGroupsLandingUrl, getGroupViewUrl } from 'utils/groupUrls';

export const GroupsSummaryWrapper = ({
  internalId,
  isMainPanel,
}: {
  internalId: string;
  isMainPanel: boolean;
}) => (
  <ErrorBoundary FallbackComponent={Fallback} onError={logBoundaryError}>
    <Suspense
      fallback={
        isMainPanel ? (
          <LoadingMainPanelSection hasHeader={true} />
        ) : (
          <LoadingSidePanelSection />
        )
      }
    >
      <GroupsSummary internalId={internalId as string} />
    </Suspense>
  </ErrorBoundary>
);

const GroupsSummary = ({ internalId }: { internalId: string }) => {
  const isPersonalView = useIsPersonalView(internalId);
  const unifiedGroupsResponse = useLazyLoadQuery<UnifiedForumsGroupsQuery>(
    UNIFIED_FORUMS_GROUPS_QUERY,
    { internalId },
  );
  const unifiedGroupsData =
    unifiedGroupsResponse?.unified?.unifiedProfile?.forums?.groups?.edges?.map(
      (item) => item?.node,
    );
  const totalCount =
    unifiedGroupsResponse?.unified?.unifiedProfile?.forums?.groups
      ?.totalCount ?? 0;

  if (!isPersonalView && (!unifiedGroupsData || !totalCount)) {
    return null;
  }

  const onClickGroup = (groupLink: string, groupName?: string | null) => {
    trackGroupCardClick(groupName ?? 'not-named', isPersonalView);
    window.open(groupLink);
  };

  const sendJoinGroupAnalytics = () => {
    trackJoinGroupClick(isPersonalView);
  };

  const displayedGroups = unifiedGroupsData
    ? unifiedGroupsData.slice(0, 4)
    : [];
  const sectionTitle =
    totalCount === 0
      ? 'GROUPS'
      : totalCount === 1
      ? `${totalCount} GROUP`
      : `${totalCount} GROUPS`;
  return (
    <SectionSummaryContainer>
      <ElevatedContainer>
        <SubSection title={sectionTitle}>
          {!unifiedGroupsData || !totalCount ? (
            <EmptyStateMessageBody
              message="Ask questions, join discussions, and find your people."
              linkTo={getGroupsLandingUrl()}
              linkType="external"
              sendAnalyticsEvent={sendJoinGroupAnalytics}
              linkText="Join a group"
            />
          ) : (
            <>
              <div css={groupsStyles.viewAllLink}>
                <Link to={`profile/${internalId}/groups`}>{`View all`}</Link>
              </div>
              <div
                data-testid="summary-groups-list"
                css={groupsStyles.groupsList}
              >
                {displayedGroups.map((group, index) => (
                  <a
                    key={index}
                    onClick={() =>
                      onClickGroup(
                        getGroupViewUrl(group?.viewHref),
                        group?.title,
                      )
                    }
                    css={groupsStyles.groupLink}
                  >
                    <div css={groupsStyles.groupItem}>
                      <div css={groupsStyles.groupImageContainer}>
                        <img src={TeamsIcon} alt="Groups Icon" />
                      </div>
                      <div css={groupsStyles.groupInfo}>
                        <div css={groupsStyles.groupName}>{group?.title}</div>
                        <div css={groupsStyles.groupMembers}>
                          {group?.groupMemberCount?.toLocaleString('en-US')}{' '}
                          {'members'}
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </SubSection>
      </ElevatedContainer>
    </SectionSummaryContainer>
  );
};

export default GroupsSummary;

const groupsStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  }),
  header: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: token('space.200'),
    width: '100%',
  }),
  groupsList: css({
    display: 'flex',
    flexDirection: 'column',
    gap: token('space.400'),
    width: '100%',
  }),
  title: css({
    fontSize: token('space.150'),
    fontWeight: token('font.weight.semibold'),
    color: token('color.text'),
    lineHeight: token('space.200'),
    fontFamily: token('font.family.brand.heading'),
  }),
  viewAllLink: css({
    color: token('color.text.brand'),
    fontSize: token('space.200'),
    lineHeight: '20px',
    fontWeight: 600,
    fontStyle: 'normal',
    marginLeft: 'auto',
    position: 'absolute',
    fontFamily: token('font.family.brand.body'),
    top: '0',
    right: '0',
  }),
  groupLink: css({
    textDecoration: 'none',
    color: token('color.link'),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-10px',
      left: '-14px',
      right: '-14px',
      bottom: '-10px',
      borderRadius: token('space.050'),
      zIndex: 0,
    },
    '&:hover::before': {
      backgroundColor: token('color.background.accent.gray.subtlest'),
    },
    '&:hover': {
      textDecoration: 'none',

      cursor: 'pointer',
    },
    '@media (max-width: 767px)': {
      '&::before': {
        left: '-8px',
        right: '-8px',
      },
    },
  }),
  groupItem: css({
    display: 'flex',
    alignItems: 'top',
    width: '100%',
    position: 'relative',
  }),
  groupImageContainer: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '53px',
    height: token('space.600'),
    backgroundColor: token('color.background.accent.gray.subtlest'),
    borderRadius: '5px',
    marginRight: token('space.150'),
    flexShrink: 0,
  }),
  groupInfo: css({
    display: 'flex',
    flexDirection: 'column',
    gap: token('space.050'),
    flex: '1',
  }),
  groupName: css({
    fontSize: token('space.200'),
    fontWeight: token('font.weight.semibold'),
    width: '237',
    lineHeight: token('space.300'),
    color: '#0F1419',
    fontFamily: token('font.family.brand.heading'),
  }),
  groupMembers: css({
    fontFamily: 'Inter',
    color: token('color.text.subtlest'),
    fontSize: '14px',
    lineHeight: token('space.250'),
    fontWeight: '400px',
  }),
  emptyState: css({
    display: 'flex',
    flexDirection: 'column',
    gap: token('space.200'),
    color: 'red',
  }),
};
